import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_BASE_URL } from './service-proxies';

// Hand-written proxy for EmployeeDashboardAppService (plain JSON, no NSwag class wrappers). HD-2026-000984.

export interface EmployeeTile {
    value: number;
    previous: number;
    changePercent: number;
}

export interface EmployeeSummary {
    totalEmployees: EmployeeTile;
    confirmed: EmployeeTile;
    probation: EmployeeTile;
    confirmationPending: EmployeeTile;
    newJoiners: EmployeeTile;
    exits: EmployeeTile;
    attritionRate: EmployeeTile;
    accountsActivated: number;
    accountsPending: number;
    activatedPercent: number;
    pendingPercent: number;
    periodFrom: string;
    periodTo: string;
    previousFrom: string;
    previousTo: string;
}

export interface HeadcountPoint {
    name: string;
    value: number;
    previousValue: number;
    changePercent: number;
    percent: number;
}

export interface EmployeeEventRow {
    employeeId: number;
    employeeName: string;
    employeeCode: string;
    department: string;
    eventDate: string;
    daysLeft: number;
    years: number;
}

export interface EmployeeEvents {
    probationEnding: EmployeeEventRow[];
    workAnniversaries: EmployeeEventRow[];
    birthdays: EmployeeEventRow[];
    marriageAnniversaries: EmployeeEventRow[];
}

export interface RecentJoineeRow {
    employeeId: number;
    employeeName: string;
    employeeCode: string;
    department: string;
    joiningDate: string;
    employmentType: string;
    accountStatus: string;
}

export interface EmployeeDashboardFilter {
    fromDate?: string;
    toDate?: string;
    branchId?: number;
    departmentId?: number;
    employmentTypeId?: number;
    status?: string;
}

@Injectable()
export class EmployeeDashboardServiceProxy {
    private baseUrl: string;

    constructor(private http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
    }

    private params(f: EmployeeDashboardFilter, extra?: { [k: string]: any }): HttpParams {
        let p = new HttpParams();
        const all: { [k: string]: any } = {};
        Object.keys(f || {}).forEach(k => all[k] = (<any>f)[k]);
        Object.keys(extra || {}).forEach(k => all[k] = (<any>extra)[k]);
        Object.keys(all).forEach(k => {
            const v = all[k];
            if (v !== undefined && v !== null && v !== '' && v !== 0) {
                p = p.set(k, '' + v);
            }
        });
        return p;
    }

    private get<T>(method: string, f: EmployeeDashboardFilter, extra?: { [k: string]: any }): Observable<T> {
        return this.http
            .get<any>(this.baseUrl + '/api/services/app/EmployeeDashboard/' + method, { params: this.params(f, extra) })
            .pipe(map(r => r.result as T));
    }

    getEmployeeSummary(f: EmployeeDashboardFilter): Observable<EmployeeSummary> {
        return this.get<EmployeeSummary>('GetEmployeeSummary', f);
    }

    getHeadcountTrend(f: EmployeeDashboardFilter, compareWith: string): Observable<HeadcountPoint[]> {
        return this.get<HeadcountPoint[]>('GetHeadcountTrend', f, { compareWith: compareWith });
    }

    getHeadcountBreakdown(f: EmployeeDashboardFilter, dimension: string, compareWith: string): Observable<HeadcountPoint[]> {
        return this.get<HeadcountPoint[]>('GetHeadcountBreakdown', f, { dimension: dimension, compareWith: compareWith });
    }

    getUpcomingEvents(f: EmployeeDashboardFilter, days: number): Observable<EmployeeEvents> {
        return this.get<EmployeeEvents>('GetUpcomingEvents', f, { days: days });
    }

    getRecentJoinees(f: EmployeeDashboardFilter, limit: number): Observable<RecentJoineeRow[]> {
        return this.get<RecentJoineeRow[]>('GetRecentJoinees', f, { limit: limit });
    }
}
